import { Box, Tooltip, Autocomplete, AutocompleteInputChangeReason } from '@mui/material';
import { makeStyles , withStyles } from 'tss-react/mui';
import { CalendarDateService, DateObject, generateDayTimeRange, TimeRangeGenerator } from '@uplift/services';
import React, { useCallback, useMemo, useState } from 'react';
import { AutocompleteProps } from '../AutocompleteInput';
import { BaseTextField } from '../BaseTextField';
import { TIME_RANGE_DATE_VALUE_FORMAT } from './constants';

export type TimeSearchSelectProps = Pick<AutocompleteProps<TimeSearchSelectOption>, 'name' | 'label'> & {
  value: string;
  setValue: (value: string) => void;
  showAfter?: DateObject;
  inclusive?: boolean;
  error?: boolean;
  onBlur?: () => void;
  minuteIncrement?: number;
  color?: 'primary' | 'secondary';
  slotsGenerator?: TimeRangeGenerator;
};

export type TimeSearchSelectOption = {
  id: string;
  value: string;
  label: string;
  disabled?: boolean;
};

type SetValueHandlerInput = {
  value: string;
  inputValue: string;
};

const useInputStyles = makeStyles()(() => ({
  root: {
    minWidth: '100px',
    height: '48px',
  },
  input: {
    paddingTop: '1px !important',
    paddingBottom: '0 !important'
  }
}));

const VALID_DATE_FORMATS = [ 'h:mma', 'h:mm', 'h:mm a', 'h:mmA', 'h:mma', 'ha', 'h', 'hmm' ];
const DATE_LABEL_FORMAT = 'h:mma';

const StyledTooltip = withStyles(Tooltip, (theme) => ({
  tooltipPlacementTop: {
    top: theme.spacing(3),
  },
  popper: {
    zIndex: 1
  }
}));

const getClosestOption = (options: TimeSearchSelectOption[], value: TimeSearchSelectOption) => {
  const allOptions = [ ...options, value ].sort((a, b) => a.value.localeCompare(b.value));
  const index = allOptions.findIndex(opt => opt.value === value.value);

  if (index === 0) {
    return null;
  }
  return allOptions[index - 1];
};

export const TimeSearchSelect: React.VFC<TimeSearchSelectProps> = ({ slotsGenerator = generateDayTimeRange, ...props }) => {
  const { classes: inputClasses } = useInputStyles();
  const [ inputValue, setInputValue ] = useState(CalendarDateService.date(props.value, TIME_RANGE_DATE_VALUE_FORMAT).format(DATE_LABEL_FORMAT));
  const range = React.useMemo(() => slotsGenerator(props.minuteIncrement, props.inclusive, props.showAfter), [ props.minuteIncrement, props.inclusive, props.showAfter, slotsGenerator ]);
  const options: TimeSearchSelectOption[] = React.useMemo(() => {
    return range.map(date => {
      const value = date.format(TIME_RANGE_DATE_VALUE_FORMAT);

      return {
        id: value,
        value,
        label: date.format(DATE_LABEL_FORMAT),
        disabled: props.showAfter && !date.isAfter(props.showAfter),
      };
    });
  }, [ range, props.showAfter ]);

  const showDateInvalidError = useMemo(() => {
    if (!inputValue.length) return false;
    const dateIsValid = CalendarDateService.date(inputValue, VALID_DATE_FORMATS).isValid();

    return !dateIsValid;
  }, [ inputValue ]);

  const onBlur = (_: React.FocusEvent<HTMLDivElement>) => {
    createOptionHandler(inputValue);
    props.onBlur?.();
  };

  const setValueHandler = ({ value, inputValue }: SetValueHandlerInput) => {
    props.setValue(value);
    setInputValue(inputValue);
  };

  const createOptionHandler = (value: string) => {
    let date = CalendarDateService.date(value, VALID_DATE_FORMATS);

    if (date.isValid()) {
      if (![ 'am', 'pm' ].some(meridiem => value.includes(meridiem)) && date.hour() <= 12) {
        if (CalendarDateService.date(props.value, TIME_RANGE_DATE_VALUE_FORMAT).hour() >= 12) {
          date = date.add(12, 'hours');
        }
      }

      setValueHandler({
        value: date.second(0).millisecond(0).format(TIME_RANGE_DATE_VALUE_FORMAT),
        inputValue: date.format(DATE_LABEL_FORMAT),
      });
    } else {
      const currentDateValue = CalendarDateService.date(props.value, TIME_RANGE_DATE_VALUE_FORMAT);

      setInputValue(currentDateValue.format(DATE_LABEL_FORMAT));
    }
  };

  const onChangeHandler = (_: React.ChangeEvent<unknown>, newValue: string | TimeSearchSelectOption) => {
    if (typeof newValue === 'string') {
      createOptionHandler(newValue);
    } else {
      setValueHandler({
        value: newValue.value,
        inputValue: newValue.label
      });
    }
  };

  const onInputChangeHandler = (_: React.ChangeEvent<unknown>, inputValue: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'reset') return;
    setInputValue(inputValue);
  };

  const value = useMemo(() => {
    let option = options.find(option => option.value === props.value);

    if (!option) {
      const date = CalendarDateService.date(props.value, TIME_RANGE_DATE_VALUE_FORMAT);

      option = {
        label: date.format(DATE_LABEL_FORMAT),
        value: props.value,
        id: props.value,
      };
    }

    return option;
  }, [ options, props.value ]);

  const onOpen = useCallback(() => {
    // why setTimeout?
    // onOpen is called as the menu is mounting, so we need a split-second before accessing the menu dom elements
    setTimeout(() => {
      let optionEl = document.querySelector(`[data-name="${value.value}"]`);

      if (!optionEl) {
        const closestOption = getClosestOption(options, value);

        if (closestOption) {
          optionEl = document.querySelector(`[data-name="${closestOption.value}"]`);
        }
      }

      optionEl?.scrollIntoView({ block: 'center' });
    }, 1);
  }, [ options, value ]);

  return (
    <Autocomplete
      onOpen={onOpen}
      filterOptions={options => options}
      fullWidth
      options={options}
      getOptionLabel={opt => typeof opt === 'string' ? opt : opt?.label}
      value={value}
      onChange={onChangeHandler}
      inputValue={inputValue}
      onInputChange={onInputChangeHandler}
      onBlur={onBlur}
      renderInput={(params) => {
        return (
          <StyledTooltip
            title="Invalid Time"
            open={showDateInvalidError}
            placement="top"
            arrow
            disableHoverListener
            disableFocusListener
            disableTouchListener
          >
            <Box>
              <BaseTextField {...params} color={props.color} error={props.error || showDateInvalidError} label={props.label} inputClasses={inputClasses} />
            </Box>
          </StyledTooltip>
        );
      }}
      renderOption={(_props, option) => {
        const label = typeof option === 'string' ? option : option?.label;
        const value = typeof option === 'string' ? option : option?.value;

        return <li {..._props}><div data-name={value}>{label}</div></li>;
      }}
      freeSolo
      disableClearable
      slotProps={{
        listbox: {
          style: { maxHeight: '180px' }, // (36 * 5 = 180) 36px is height of each list item
        }
      }}
    />
  );
};
